/**
 * Auth state — minimal Zustand store wrapping localStorage tokens.
 *
 * Tokens are the source of truth (set/cleared in lib/auth.ts). The store's
 * derived `user`/`tenant`/`role` are decoded from the access token on hydrate
 * and on `setSession`. After login, calling /me/ refreshes the email field
 * (JWT only carries user_id, not email).
 */

import { create } from "zustand";

import { clearTokens, getAccessToken, setTokens } from "@/lib/auth";
import { decodeJWT, isExpired } from "@/lib/jwt";
import type { AuthSession, Role, Tenant, User } from "@/types/api";

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  role: Role | null;
  isHydrated: boolean;

  setSession: (s: AuthSession) => void;
  clearSession: () => void;
  hydrate: () => void;
  setUser: (u: User) => void; // for /me/ refresh
  setTenant: (t: Tenant | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  role: null,
  isHydrated: false,

  setSession: (s) => {
    setTokens(s.tokens.access, s.tokens.refresh);
    set({ user: s.user, tenant: s.tenant, role: s.role, isHydrated: true });
  },

  clearSession: () => {
    clearTokens();
    set({ user: null, tenant: null, role: null, isHydrated: true });
  },

  hydrate: () => {
    const token = getAccessToken();
    if (!token || isExpired(token)) {
      // Either no token, or expired — clear and mark hydrated
      clearTokens();
      set({ user: null, tenant: null, role: null, isHydrated: true });
      return;
    }
    const claims = decodeJWT(token);
    if (!claims) {
      clearTokens();
      set({ user: null, tenant: null, role: null, isHydrated: true });
      return;
    }
    set({
      // We don't have email from the JWT — caller fetches /me/ to fill it
      user: { id: claims.user_id, email: "" },
      tenant:
        claims.active_tenant_id && claims.tenant_slug
          ? {
              id: claims.active_tenant_id,
              slug: claims.tenant_slug,
              display_name: "",
            }
          : null,
      role: claims.role ?? null,
      isHydrated: true,
    });
  },

  setUser: (u) => set({ user: u }),
  setTenant: (t) => set({ tenant: t }),
}));

export const useIsAuthenticated = () => useAuthStore((s) => s.user !== null);
